import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { brands } from '../data/brands';
import { services } from '../data/services';
import { serviceTypes } from '../data/serviceTypes';
import { capitalizeWords } from '../utils/stringUtils';
import ActionButtons from '../components/shared/ActionButtons';
import StateLocationSelector from '../components/locations/StateLocationSelector';

export default function StateServiceDetail() {
  const { state, service: serviceId } = useParams();
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  const formattedState = capitalizeWords(state?.replace(/-/g, ' ') || '');
  const service = services.find(s => s.id === serviceId);
  const brand = brands.find(b => b.id === selectedBrand);
  const serviceTypesList = service ? serviceTypes[service.id] || [] : [];

  if (!service || !state) return null;

  return (
    <>
      <Helmet>
        <title>{`${service.name} Services in ${formattedState} | Parakh Services`}</title>
        <meta 
          name="description" 
          content={`Professional ${service.name.toLowerCase()} services in ${formattedState}. Expert technicians and same-day service available.`} 
        />
      </Helmet>

      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {service.name} Services in {formattedState}
            </h1>
            <p className="text-xl text-gray-600">
              Professional {service.name.toLowerCase()} services with expert technicians
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
              {/* Brand Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Brand
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Brands</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location Selection */}
              <StateLocationSelector
                state={state}
                onCityChange={setSelectedCity}
                onAreaChange={setSelectedArea}
                selectedBrand={selectedBrand}
              />
            </div>
          </div>

          {/* Service Types List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {serviceTypesList.map((type) => (
              <div 
                key={type.id}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-blue-500 transition-all duration-300 hover:shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {brand ? `${brand.name} ` : ''}{type.name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {type.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">
                    90-Day Warranty
                  </span>
                  <span className="text-sm bg-green-50 text-green-700 px-2 py-1 rounded">
                    Same Day Service
                  </span>
                </div>
                {selectedCity && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">
                      Available in {selectedArea && `${selectedArea}, `}{selectedCity}, {formattedState}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              Book {service.name} Service
              {selectedCity && ` in ${selectedCity}, ${formattedState}`}
            </h3>
            <p className="text-lg opacity-90 mb-8">
              Our expert technicians are available 24/7 for all types of repairs
            </p>
            <ActionButtons 
              className="justify-center"
              location={{
                state: state,
                city: selectedCity,
                area: selectedArea
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}